import { roundMoney } from '../../../../utils/money';
import React from 'react';
import { flushSync } from 'react-dom';
import { notifications } from '@mantine/notifications';
import { CASH_COUNT_COINS, computeCashCountTotal } from 'shared-models/browser';
import type { PrinterView, SessionCashCountView } from 'shared-models/browser';
import { sessionCashCountService } from '../../../../services/sessions/session-cash-count.service';
import { printersService } from '../../../../services/printers/printers.service';
import { captureReceiptSnapshot } from '../receipt/shared/receipt.utils';
import { resolveReceiptPrintTarget } from '../../hooks/fixed-printer.util';
import {
  denominationKey,
  inputsFromQuantities,
  quantitiesFromInputs,
  resolveStationName,
  sameQuantities,
  sanitizeQuantityInput,
} from './cash-count.utils';
import type { CashCountInputs } from './cash-count.utils';

export type UseCashCountControllerArgs = {
  tenantId: string;
  employeeSession: string;
  sessionId: number;
  expectedCash: number | null;
  onSaved?: (count: SessionCashCountView) => void;
  onSavedAndPrinted?: () => void;
};

const toast = (color: 'green' | 'red', title: string, message: string) =>
  notifications.show({ color, title, message, autoClose: 3000 });

// Summary: Cash-count modal state — load on open, live totals, save→print, printer picking.
export function useCashCountController({ tenantId, employeeSession, sessionId, expectedCash, onSaved, onSavedAndPrinted }: UseCashCountControllerArgs) {
  const [count, setCount] = React.useState<SessionCashCountView | null>(null);
  const [inputs, setInputs] = React.useState<CashCountInputs>(() => inputsFromQuantities(null));
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [printing, setPrinting] = React.useState(false);
  const [coinsOpen, setCoinsOpen] = React.useState(false);
  const [printers, setPrinters] = React.useState<PrinterView[]>([]);
  const [pickCandidates, setPickCandidates] = React.useState<PrinterView[] | null>(null);
  const receiptRef = React.useRef<HTMLDivElement>(null);
  const printInFlightRef = React.useRef(false);
  const saveInFlightRef = React.useRef(false);
  const closeAfterPrintRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    Promise.all([
      sessionCashCountService.get(tenantId, sessionId, employeeSession),
      printersService.listActive(tenantId, employeeSession).catch(() => [] as PrinterView[]),
    ])
      .then(([loaded, activePrinters]) => {
        if (cancelled) return;
        setCount(loaded);
        setPrinters(activePrinters);
        setInputs(inputsFromQuantities(loaded?.quantities ?? null));
        setCoinsOpen(CASH_COUNT_COINS.some((coin) => (loaded?.quantities[denominationKey(coin)] ?? 0) > 0));
      })
      .catch((error: unknown) => {
        if (!cancelled) toast('red', 'שגיאה', error instanceof Error ? error.message : 'טעינת הספירה נכשלה');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, sessionId, employeeSession]);

  const quantities = React.useMemo(() => quantitiesFromInputs(inputs), [inputs]);
  const countedTotal = computeCashCountTotal(quantities);
  const isDirty = count === null || !sameQuantities(quantities, count.quantities);
  // Unchanged saved sheet shows the server snapshot so screen and printed copy agree.
  const snapshot = isDirty ? null : count;
  const shownExpected = snapshot ? snapshot.expectedCash : expectedCash;
  const difference = snapshot ? snapshot.difference : expectedCash == null ? null : roundMoney(countedTotal - expectedCash);

  const setQuantityInput = React.useCallback((key: keyof CashCountInputs, raw: string) => {
    setInputs((previous) => ({ ...previous, [key]: sanitizeQuantityInput(raw) }));
  }, []);

  const printTo = async (printerId: number) => {
    if (printInFlightRef.current || !receiptRef.current) return;
    printInFlightRef.current = true;
    setPrinting(true);
    try {
      const image = await captureReceiptSnapshot(receiptRef.current);
      if (!image) {
        toast('red', 'שגיאה', 'לא ניתן ליצור תמונה להדפסה');
        return;
      }
      await sessionCashCountService.print(tenantId, sessionId, printerId, image, employeeSession);
      setCount((previous) => previous && { ...previous, printCount: previous.printCount + 1, lastPrintedAt: new Date().toISOString() });
      setPickCandidates(null);
      toast('green', 'הדפסה', 'ספירת השטרות נשלחה להדפסה');
      if (closeAfterPrintRef.current) {
        closeAfterPrintRef.current = false;
        onSavedAndPrinted?.();
      }
    } catch (error: unknown) {
      toast('red', 'ההדפסה נכשלה', error instanceof Error ? error.message : 'שגיאה לא ידועה');
    } finally {
      printInFlightRef.current = false;
      setPrinting(false);
    }
  };

  // Billable/sole/fixed auto-prints; 2+ candidates open the picker; none = toast per branch.
  const startPrint = async (saved: boolean) => {
    const target = resolveReceiptPrintTarget(printers);
    if (target.kind === 'print') {
      await printTo(target.printerId);
    } else if (target.kind === 'pick') {
      setPickCandidates(target.candidates ?? printers);
    } else {
      toast('red', 'אין מדפסות פעילות', saved ? 'הספירה נשמרה ללא הדפסה' : 'אין מדפסת זמינה להדפסה');
    }
  };

  // Changed sheet → save (server resets print_count → original); unchanged → print again as a copy.
  const saveAndPrint = async () => {
    if (saveInFlightRef.current) return;
    saveInFlightRef.current = true;
    closeAfterPrintRef.current = true;
    try {
      if (!isDirty) {
        await startPrint(false);
        return;
      }
      setSaving(true);
      let saved: SessionCashCountView;
      try {
        saved = await sessionCashCountService.upsert(
          tenantId,
          sessionId,
          { quantities, stationName: resolveStationName(tenantId, printers) },
          employeeSession,
        );
      } catch (error: unknown) {
        setSaving(false);
        toast('red', 'השמירה נכשלה', error instanceof Error ? error.message : 'שגיאה לא ידועה');
        return;
      }
      // Commit synchronously so the offscreen receipt renders the SERVER row before capture.
      flushSync(() => setCount(saved));
      setSaving(false);
      onSaved?.(saved);
      await startPrint(true);
    } finally {
      saveInFlightRef.current = false;
    }
  };

  return {
    count,
    inputs,
    loading,
    saving,
    printing,
    coinsOpen,
    setCoinsOpen,
    pickCandidates,
    closePicker: () => { if (!printing) { closeAfterPrintRef.current = false; setPickCandidates(null); } },
    receiptRef,
    countedTotal,
    expectedCash: shownExpected,
    difference,
    setQuantityInput,
    saveAndPrint,
    printTo,
  };
}
